import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { tags as t } from '@lezer/highlight';

// Dark+: VS Code built-in Dark+ theme mapping of the nowline TextMate grammar scopes.
// keyword.control → #C586C0 (purple override, not the usual blue)
// constant.language → #569CD6, entity.other.attribute-name → #9CDCFE
// variable.other → #9CDCFE, string.quoted.double → #CE9178
// comment → #6A9955 (no italic), constant.numeric.* → #B5CEA8
// markup.underline.link → underline, inherit foreground
export const darkHighlightStyle = HighlightStyle.define([
    { tag: t.keyword, color: '#C586C0' },
    { tag: t.atom, color: '#569CD6' },
    { tag: t.attributeName, color: '#9CDCFE' },
    { tag: t.variableName, color: '#9CDCFE' },
    { tag: t.string, color: '#CE9178' },
    { tag: t.comment, color: '#6A9955' },
    { tag: t.number, color: '#B5CEA8' },
    { tag: t.link, textDecoration: 'underline' },
]);

// Light+: VS Code built-in Light+ theme mapping.
// keyword.control → #AF00DB, constant.language → #0000FF
// entity.other.attribute-name → #E50000 (red in light, unlike dark)
// variable.other → #001080, string.quoted.double → #A31515
// comment → #008000, constant.numeric.* → #098658
export const lightHighlightStyle = HighlightStyle.define([
    { tag: t.keyword, color: '#AF00DB' },
    { tag: t.atom, color: '#0000FF' },
    { tag: t.attributeName, color: '#E50000' },
    { tag: t.variableName, color: '#001080' },
    { tag: t.string, color: '#A31515' },
    { tag: t.comment, color: '#008000' },
    { tag: t.number, color: '#098658' },
    { tag: t.link, textDecoration: 'underline' },
]);

// Editor chrome: background, gutters, selection, active line, cursor.
// Tied to the app's design token palette (#0a0a14 / #14141e / #f0f0f5 etc.)
// so the editor interior stays visually coherent with the surrounding chrome.
const darkEditorTheme = EditorView.theme(
    {
        '&': { background: '#0e0e1a', color: '#f0f0f5' },
        '.cm-content': { caretColor: '#f0f0f5' },
        '.cm-gutters': {
            background: '#0a0a14',
            color: '#a0a0b0',
            borderRight: '1px solid #2a2a3a',
        },
        '.cm-lineNumbers': { color: '#a0a0b0' },
        '.cm-foldGutter': { color: '#a0a0b0' },
        '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection': {
            background: 'rgba(100,100,220,0.35)',
        },
        '.cm-activeLine': { background: 'rgba(255,255,255,0.04)' },
        '.cm-activeLineGutter': {
            background: 'rgba(255,255,255,0.04)',
            color: '#f0f0f5',
        },
        '.cm-cursor, .cm-dropCursor': { borderLeftColor: '#f0f0f5' },
        '.cm-tooltip': {
            background: '#1e1e2e',
            border: '1px solid #2a2a3a',
            color: '#f0f0f5',
        },
    },
    { dark: true },
);

const lightEditorTheme = EditorView.theme(
    {
        '&': { background: '#ffffff', color: '#1a1a2e' },
        '.cm-content': { caretColor: '#1a1a2e' },
        '.cm-gutters': {
            background: '#f5f5f8',
            color: '#5a5a6a',
            borderRight: '1px solid #c0c0c0',
        },
        '.cm-lineNumbers': { color: '#5a5a6a' },
        '.cm-foldGutter': { color: '#5a5a6a' },
        '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection': {
            background: 'rgba(26,78,216,0.15)',
        },
        '.cm-activeLine': { background: 'rgba(0,0,0,0.03)' },
        '.cm-activeLineGutter': {
            background: 'rgba(0,0,0,0.03)',
            color: '#1a1a2e',
        },
        '.cm-cursor, .cm-dropCursor': { borderLeftColor: '#1a1a2e' },
        '.cm-tooltip': {
            background: '#ffffff',
            border: '1px solid #c0c0c0',
            color: '#1a1a2e',
        },
    },
    { dark: false },
);

/**
 * Returns the editor theme extensions (visual chrome + syntax colours) for the
 * given colour scheme. Load these into a single Compartment so both layers swap
 * atomically when the app theme changes — avoids a flash where chrome updates
 * before syntax highlighting catches up.
 */
export function buildThemeExtensions(theme: 'light' | 'dark'): Extension[] {
    if (theme === 'dark') {
        return [darkEditorTheme, syntaxHighlighting(darkHighlightStyle)];
    }
    return [lightEditorTheme, syntaxHighlighting(lightHighlightStyle)];
}
